/**
 * @file link.modal.tsx
 * @description Модальное окно для добавления новых ссылок в систему.
 * Содержит форму с полями для ввода URL ссылки и её наименования.
 * Использует react-hook-form для валидации и обработки данных формы.
 * Поддерживает редактирование существующих ссылок через передачу item prop.
 * @author AI Assistant
 * @created 2025-09-03
 * @updated 2025-09-03
 * @dependencies [react, @src/hooks, @consta/uikit/Modal, @components/ui/layout/modal/components, @components/ui/base/button, react-hook-form, @components/form-controls/text-field]
 * @related-files [@components/ui/layout/modal/components/header.tsx, @components/ui/base/button/button.component.tsx, @components/form-controls/text-field/text-field.component.tsx]
 */

import { useModalQueue } from '@src/hooks';
import { Modal } from '@consta/uikit/Modal';
import { Header } from '@components/ui/layout/modal/components';
import { Button } from '@components/ui/base/button';
import { useForm } from 'react-hook-form';
import { TextField } from '@components/form-controls/text-field';
import { TAttachment } from '@src/types';

export const LinkModal = ({
    isShow,
    setIsShow,
    addItemHandler,
    item = null,
    title = 'Добавление новой сcылки'
}: {
    isShow: boolean;
    setIsShow: React.Dispatch<React.SetStateAction<boolean>>;
    addItemHandler: (data: TAttachment) => void;
    item?: TAttachment | null;
    title?: string;
}) => {
    const closeModalHandler = useModalQueue(setIsShow);
    const {
        handleSubmit,
        control,
        formState: { errors }
    } = useForm<TAttachment>({ defaultValues: item || undefined });

    const onSubmit = (data: TAttachment) => {
        addItemHandler(data);
        closeModalHandler();
    };

    return (
        <Modal
            isOpen={isShow}
            hasOverlay={false}
            onClickOutside={(e) => !(e?.target as HTMLElement).closest('.dhx_popup') && closeModalHandler()}
            position="center"
            onEsc={closeModalHandler}
            className="new-link-modal w-[60.0179vmin] min-h-[18vmin]"
        >
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="new-link-modal_container flex flex-col h-full p-[3.1756vmin] gap-default"
            >
                <Header title={title} closeBtnClickHandler={() => closeModalHandler()} />

                <div className="flex flex-col gap-default">
                    <TextField
                        type="textarea"
                        minRows={5}
                        control={control}
                        label="Ссылка"
                        name="link"
                        autoFocus={true}
                        rules={{ required: true }}
                        shouldUnregister={true}
                        status={errors.link ? 'alert' : undefined}
                    />

                    <TextField
                        control={control}
                        label="Имя"
                        name="name"
                        rules={{ required: true }}
                        shouldUnregister={true}
                        status={errors.name ? 'alert' : undefined}
                    />
                </div>

                <div className="flex flex-row w-full justify-between items-end flex-1 h-full">
                    <Button
                        className="text-[1.4819vmin]"
                        label="Отмена"
                        onClick={() => closeModalHandler()}
                        view="clear"
                    />

                    <div>
                        <Button
                            className="mr-[0.8468vmin] text-[1.4819vmin] h-[3.387vmin]"
                            label="Сохранить"
                            type="submit"
                            onClick={handleSubmit(onSubmit)}
                            view="primary"
                        />
                    </div>
                </div>
            </form>
        </Modal>
    );
};
